import { Card, Box, Icon, Text, SimpleGrid, Button, useToast } from "@chakra-ui/react";
import { LuLeaf } from "react-icons/lu";
import { TbDisabled } from "react-icons/tb";
import { ImExit } from "react-icons/im";
import { FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import * as signalR from "@microsoft/signalr";
import { useLoaderData, useNavigate, redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAnalytics, logEvent } from "firebase/analytics";
import React, { useContext, useEffect, useState } from "react";
import { SignalRContext } from "./SignalRContext";
import { useRevalidator } from "react-router-dom";
import { Device } from "../components/Device.jsx";
import axios from "axios";
import alarmSound from '../Sound/CameraAlarm.mp3';
import click from '../Sound/shortclick.mp3';
import chime from '../Sound/chime.mp3';
import { useSound } from './SoundContext.jsx';

export const Dashboard = () => {
    const { message } = useContext(SignalRContext);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const revalidator = useRevalidator();
    const analytics = getAnalytics();
    const toast = useToast();
    const { soundEnabled } = useSound();

    const [retry, setRetry] = useState(false);
    const [audio] = useState(() => {
        const a = new Audio(alarmSound);
        a.loop = true;
        return a;
    });
    const [isPlaying, setIsPlaying] = useState(false);

    logEvent(analytics, "screen_view", "Dashboard");

    const [dev, setDev] = useState([]);
    const devicesFromApi = useLoaderData() || [];
    const [devices, setDevices] = useState([]);
    const [combinedData, setCombinedData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // New state for all devices and pump faults
    const [allDevices, setAllDevices] = useState([]);
    const [faultyFreshWaterPumps, setFaultyFreshWaterPumps] = useState([]);
    const [faultySaltWaterPumps, setFaultySaltWaterPumps] = useState([]);

    const playClickSound = () => {
        const audio = new Audio(click);
        audio.play();
        audio.onended = () => { }
    };

    useEffect(() => {
        return () => {
            audio.pause();
            audio.currentTime = 0;
        };
    }, []);

    // Status label configurations (same as in FaultPumpDetails)
    const getFreshWaterStatusLabels = (battery) => {
        let pump = Math.floor(battery / 10);
        if (pump === 9) return [{ id: 1, label: "DIRECT FEED", bitPosition: 0, positiveIsGood: false }];
        if (pump === 7) return null;
        if (pump === 3) return [
            { id: 1, label: "控制電源", bitPosition: 8, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 1, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 0, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 3, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 2, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 7, positiveIsGood: false },
            { id: 7, label: "天台水缸高水位", bitPosition: 6, positiveIsGood: false },
            { id: 8, label: "地下水缸低水位", bitPosition: 5, positiveIsGood: false },
            { id: 9, label: "地下水缸高水位", bitPosition: 4, positiveIsGood: false }
        ];
        if (pump === 2) return [
            { id: 1, label: "控制電源", bitPosition: 5, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 1, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 0, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 3, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 2, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 4, positiveIsGood: false }
        ];
        if (pump === 1) return [
            { id: 1, label: "一號泵運行", bitPosition: 3, positiveIsGood: true },
            { id: 2, label: "一號泵故障", bitPosition: 4, positiveIsGood: false },
            { id: 3, label: "二號泵運行", bitPosition: 1, positiveIsGood: true },
            { id: 4, label: "二號泵故障", bitPosition: 2, positiveIsGood: false },
            { id: 5, label: "天台水缸低水位", bitPosition: 0, positiveIsGood: false }
        ];
        return [
            { id: 1, label: "控制電源", bitPosition: 8, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 1, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 0, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 3, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 2, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 7, positiveIsGood: false },
            { id: 7, label: "天台水缸高水位", bitPosition: 6, positiveIsGood: false },
            { id: 8, label: "地下水缸低水位", bitPosition: 5, positiveIsGood: false },
            { id: 9, label: "地下水缸高水位", bitPosition: 4, positiveIsGood: false }
        ];
    };

    const getSaltWaterStatusLabels = (battery) => {
        const unitDigit = Math.abs(battery) % 10;
        if (unitDigit === 9) return [{ id: 1, label: "Direct Feed", bitPosition: 0, positiveIsGood: true }];
        if (unitDigit === 7) return null;
        if (unitDigit === 3) return [
            { id: 1, label: "控制電源", bitPosition: 17, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 12, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 16, positiveIsGood: false },
            { id: 7, label: "天台水缸高水位", bitPosition: 15, positiveIsGood: false },
            { id: 8, label: "地下水缸低水位", bitPosition: 14, positiveIsGood: false },
            { id: 9, label: "地下水缸高水位", bitPosition: 13, positiveIsGood: false }
        ];
        if (unitDigit === 2) return [
            { id: 1, label: "控制電源", bitPosition: 14, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 12, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 13, positiveIsGood: false }
        ];
        if (unitDigit === 1) return [
            { id: 1, label: "一號泵運行", bitPosition: 12, positiveIsGood: true },
            { id: 2, label: "一號泵故障", bitPosition: 13, positiveIsGood: false },
            { id: 3, label: "二號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 4, label: "二號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 5, label: "天台水缸低水位", bitPosition: 9, positiveIsGood: false }
        ];
        if (unitDigit === 5) return [
            { id: 1, label: "控制電源", bitPosition: 12, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 8, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 7, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 11, positiveIsGood: false }
        ];
        if (unitDigit === 4) return [
            { id: 1, label: "一號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 2, label: "一號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 3, label: "二號泵運行", bitPosition: 8, positiveIsGood: true },
            { id: 4, label: "二號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 5, label: "天台水缸低水位", bitPosition: 7, positiveIsGood: false }
        ];
        return [
            { id: 1, label: "控制電源", bitPosition: 17, positiveIsGood: true },
            { id: 2, label: "一號泵運行", bitPosition: 10, positiveIsGood: true },
            { id: 3, label: "一號泵故障", bitPosition: 9, positiveIsGood: false },
            { id: 4, label: "二號泵運行", bitPosition: 12, positiveIsGood: true },
            { id: 5, label: "二號泵故障", bitPosition: 11, positiveIsGood: false },
            { id: 6, label: "天台水缸低水位", bitPosition: 16, positiveIsGood: false },
            { id: 7, label: "天台水缸高水位", bitPosition: 15, positiveIsGood: false },
            { id: 8, label: "地下水缸低水位", bitPosition: 14, positiveIsGood: false },
            { id: 9, label: "地下水缸高水位", bitPosition: 13, positiveIsGood: false }
        ];
    };

    const checkForFaults = (device, statusLabels) => {
        if (!statusLabels || !device.status) return [];

        const faults = [];
        statusLabels.forEach(label => {
            if (!label.positiveIsGood) {
                const bitValue = device.status.charAt(label.bitPosition);
                if (bitValue === '1') {
                    faults.push({
                        label: label.label,
                        bitPosition: label.bitPosition,
                        deviceName: device.deviceName,
                        block: device.block,
                        deviceType: device.deviceType
                    });
                }
            }
        });

        return faults;
    };

    const analyzePumps = (devices) => {
        const freshWaterFaults = [];
        const saltWaterFaults = [];

        devices.forEach(device => {
            if (device.deviceType === 2 || device.deviceType === 3) {
                // Check fresh water pump faults
                const freshLabels = getFreshWaterStatusLabels(device.battery || 0);
                const freshFaults = checkForFaults(device, freshLabels);
                if (freshFaults.length > 0) {
                    freshWaterFaults.push({
                        device,
                        faults: freshFaults
                    });
                }

                // Check salt water pump faults
                const saltLabels = getSaltWaterStatusLabels(device.battery || 0);
                const saltFaults = checkForFaults(device, saltLabels);
                if (saltFaults.length > 0) {
                    saltWaterFaults.push({
                        device,
                        faults: saltFaults
                    });
                }
            }
        });

        return { freshWaterFaults, saltWaterFaults };
    };

    const updatePumpFaults = (deviceList) => {
        const { freshWaterFaults, saltWaterFaults } = analyzePumps(deviceList);
        setFaultyFreshWaterPumps(freshWaterFaults);
        setFaultySaltWaterPumps(saltWaterFaults);
    };

    // Update all devices when SignalR message arrives
    useEffect(() => {
        if (!message) return;

        const temp = Array.isArray(message.devices) ? message.devices : [];

        // Update all devices (not filtered) for pump fault detection
        setAllDevices(prevAllDevices => {
            const updatedAllDevices = prevAllDevices.map(device => {
                const updatedDevice = temp.find(d => d.deviceId === device.deviceId);
                return updatedDevice ? { ...device, ...updatedDevice } : device;
            });

            // Update pump faults with the complete device list
            updatePumpFaults(updatedAllDevices);
            return updatedAllDevices;
        });

        // For emergency counts, use filtered devices (deviceId < 9)
        const emergencyDevices = temp.filter(e => e.deviceId < 9);
        setDevices(emergencyDevices);

        const emergencyCount = emergencyDevices
            .filter(e => e.deviceType === 1 && (e.alive === 1 || e.status === "1"))
            .length;

        let elevatorResult = 0;
        const elevatorAlarmDetectionCount = emergencyDevices
            .filter((e) => {
                for (let x = 0; x < 4; x++) {
                    if (e.status && e.status[x] === "1") {
                        elevatorResult++;
                    }
                }
                return elevatorResult;
            }).length;

        let fireResult = 0;
        const fireAlarmDetectionCount = emergencyDevices
            .filter((e) => {
                for (let x = 4; x < 6; x++) {
                    if (e.status && e.status[x] === "1") {
                        fireResult++;
                    }
                }
                return fireResult;
            }).length;

        if (soundEnabled && ((emergencyCount > 0) || (elevatorAlarmDetectionCount > 0) || (fireAlarmDetectionCount > 0))) {
            audio.play().catch(err => {
                console.error("Audio blocked:", err);
                toast({
                    title: "Audio playback blocked",
                    description: "Please enable audio permissions for this site",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            });
            setIsPlaying(true);
        } else {
            audio.pause();
            setIsPlaying(false);
        }

        // Update combinedData for type 1 devices
        setCombinedData(prevData => {
            return prevData.map(item => {
                const updatedDevice = temp.find(d => d.deviceId === item.deviceId);
                return updatedDevice ? { ...item, ...updatedDevice } : item;
            });
        });

        revalidator.revalidate();
    }, [message, soundEnabled]);

    useEffect(() => {
        isPlaying && soundEnabled ? audio.play() : audio.pause();
    }, [isPlaying, soundEnabled]);

    useEffect(() => {
        const httpClient1 = axios.create({
            baseURL: 'https://villarhapsodyiot.org',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const httpClient2 = axios.create({
            baseURL: 'https://villarhapsodyiot.org',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const fetchAndCombineData = async () => {
            try {
                const [devicesResponse, detailsResponse] = await Promise.all([
                    httpClient1.get('/Devices/GetDevicesOnAllBlocks'),
                    httpClient2.get('/Devices/GetManagementBlocks')
                ]);

                const allDevicesData = devicesResponse.data;
                const details = detailsResponse.data;

                // Set all devices for pump fault detection
                setAllDevices(allDevicesData);

                // Set combinedData (for type 1 devices only) for the device list at bottom
                const type1Devices = allDevicesData.filter(d => d.deviceType === 1);
                const mergedData = type1Devices.map(device => {
                    const detail = details.find(d => d.deviceId === device.deviceId);
                    return {
                        ...device,
                        ...(detail && Object.fromEntries(
                            Object.entries(detail).filter(([key]) => key !== 'device_Id')
                        ))
                    };
                });

                setCombinedData(mergedData);

                // Analyze all devices for pump faults
                updatePumpFaults(allDevicesData);

                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchAndCombineData();
    }, []);

    let result = [];
    if (message?.flag === false) {
        result = devicesFromApi || [];
    } else {
        result = devices || [];
    }

    const emergncyTenant = result.filter((e) => e.deviceType === 1);
    const elevatorAlarmDetectionCount = result.filter((e) => {
        let result = 0;
        for (let x = 0; x < 4; x++) {
            if (e.status && e.status[x] === "1") {
                result++;
            }
        }
        return result;
    });

    const fireAlarmDetectionCount = result.filter((e) => {
        let result = 0;
        for (let x = 4; x < 6; x++) {
            if (e.status && e.status[x] === "1") {
                result++;
            }
        }
        return result;
    });

    const emergncyTenantCount = emergncyTenant.filter((e) => e.alive === 1);

    // Calculate fault counts from the dedicated pump fault state
    const freshWaterFaultCount = faultyFreshWaterPumps.reduce((sum, pump) => sum + pump.faults.length, 0);
    const saltWaterFaultCount = faultySaltWaterPumps.reduce((sum, pump) => sum + pump.faults.length, 0);

    const onClickElevator = () => {
        playClickSound();
        navigate("/dashboard/elevators", { state: null });
    };

    const onClickFireAlarms = () => {
        playClickSound();
        navigate("/dashboard/firealarms", { state: null });
    };

    const onClickTenantemergency = () => {
        playClickSound();
        navigate("/dashboard/tenantemergences", { state: null });
    };

    const onClickPumpFaults = () => {
        playClickSound();
        navigate("/dashboard/pump-faults", { state: null });
    };

    return (
        <Box mt="0px" mx="20px">
            <SimpleGrid columns={[1, 1, 1, 3, 3]} mt="20px" spacing="20px">
                <Card
                    onClick={onClickElevator}
                    w="full"
                    h="120px"
                    backgroundColor="gray.50"
                    _hover={{ bg: "gray.100" }}
                >
                    <Box display="flex" alignItems="center" align="center" pt="2" ml="4">
                        <Icon as={LuLeaf} boxSize={4} />
                        <Text ml="1" fontSize={["lg", "lg", "lg", "lg"]}>
                            {t("text_elevator_alarm")}
                        </Text>
                    </Box>
                    <Box align="center">
                        {elevatorAlarmDetectionCount.length > 0 ? (
                            <Text
                                mt="4"
                                color="red.500"
                                fontSize={["lg", "lg", "2xl", "2xl"]}
                            >
                                {t("test_elevator_alarm_detected")}
                            </Text>
                        ) : (
                            <Text
                                mt="4"
                                color="green.500"
                                fontSize={["lg", "lg", "2xl", "2xl"]}
                            >
                                {t("text_normal")}
                            </Text>
                        )}
                    </Box>
                </Card>

                <Card
                    onClick={onClickFireAlarms}
                    w="full"
                    h="120px"
                    backgroundColor="gray.50"
                    _hover={{ bg: "gray.100" }}
                >
                    <Box display="flex" alignItems="center" align="center" pt="2" ml="4">
                        <Icon as={TbDisabled} boxSize={4} />
                        <Text ml="1" fontSize={["lg", "lg", "lg", "lg"]}>
                            {t("text_fire_alarm")}
                        </Text>
                    </Box>
                    <Box align="center">
                        {fireAlarmDetectionCount.length > 0 ? (
                            <Text
                                mt="4"
                                color="red.500"
                                fontSize={["lg", "lg", "2xl", "2xl"]}
                            >
                                {t("test_fire_alarm_detected")}
                            </Text>
                        ) : (
                            <Text
                                mt="4"
                                color="green.500"
                                fontSize={["lg", "lg", "2xl", "2xl"]}
                            >
                                {t("text_normal")}
                            </Text>
                        )}
                    </Box>
                </Card>

                <Card
                    onClick={onClickTenantemergency}
                    w="full"
                    h="120px"
                    backgroundColor="gray.50"
                    _hover={{ bg: "gray.100" }}
                >
                    <Box display="flex" alignItems="center" align="center" pt="2" ml="4">
                        <Icon as={ImExit} boxSize={4} />
                        <Text fontSize={["lg", "lg", "lg", "lg"]}>
                            {t("text_tenant_intercom")}
                        </Text>
                    </Box>
                    <Box align="center">
                        {emergncyTenantCount.length > 0 ? (
                            <Text
                                mt="4"
                                color="red.500"
                                fontSize={["lg", "lg", "2xl", "2xl"]}
                            >
                                {t("text_tenant_intercom_alarm_detected")}
                            </Text>
                        ) : (
                            <Text
                                mt="4"
                                color="green.500"
                                fontSize={["lg", "lg", "2xl", "2xl"]}
                            >
                                {t("text_normal")}
                            </Text>
                        )}
                    </Box>
                </Card>
            </SimpleGrid>
            <Box mt="20px">
                <SimpleGrid columns={[1, 1, 1, 2, 2]} spacing="40px">
                    <Card
                        onClick={onClickPumpFaults}
                        w="full"
                        h="120px"
                        backgroundColor="gray.50"
                        _hover={{ bg: "gray.100" }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            align="center"
                            pt="2"
                            ml="4"
                        >
                            <Text fontSize={["lg", "lg", "lg", "lg"]}>
                                {t("text_fresh_water_pump_status")}
                            </Text>
                        </Box>
                        <Box align="center">
                            {freshWaterFaultCount > 0 ? (
                                <Text
                                    mt="4"
                                    color="red.500"
                                    fontSize={["lg", "lg", "2xl", "2xl"]}
                                >
                                    {freshWaterFaultCount} 個故障
                                </Text>
                            ) : (
                                <Text
                                    mt="4"
                                    color="green.500"
                                    fontSize={["lg", "lg", "2xl", "2xl"]}
                                >
                                    正常
                                </Text>
                            )}
                        </Box>
                    </Card>

                    <Card
                        onClick={onClickPumpFaults}
                        w="full"
                        h="120px"
                        backgroundColor="gray.50"
                        _hover={{ bg: "gray.100" }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            align="center"
                            pt="2"
                            ml="4"
                        >
                            <Text fontSize={["lg", "lg", "lg", "lg"]}>
                                {t("text_flush_water_pump_status")}
                            </Text>
                        </Box>
                        <Box align="center">
                            {saltWaterFaultCount > 0 ? (
                                <Text
                                    mt="4"
                                    color="red.500"
                                    fontSize={["lg", "lg", "2xl", "2xl"]}
                                >
                                    {saltWaterFaultCount} 個故障
                                </Text>
                            ) : (
                                <Text
                                    mt="4"
                                    color="green.500"
                                    fontSize={["lg", "lg", "2xl", "2xl"]}
                                >
                                    正常
                                </Text>
                            )}
                        </Box>
                    </Card>
                </SimpleGrid>
            </Box>
            <SimpleGrid columns={[2, 2, 2, 2, 3, 4, 4]} spacing="10px">
                {combinedData.map((e) => (
                    <Box mt="10px" mx="20px" key={e.deviceId}>
                        <Device device={e} />
                    </Box>
                ))}
            </SimpleGrid>
        </Box>
    );
};

const domain = process.env.REACT_APP_API_SERVER_URL;
const getSensorDataApi = domain + "/Devices/GetDevicesOnAllBlocks";
const accessToken = sessionStorage.getItem("accessToken");

const requestOptions = {
    headers: {
        Authorization: `Bearer ${accessToken}`,
    },
};

export const sensorDataLoader = async () => {
    const analytics = getAnalytics();
    try {
        const response = await fetch(getSensorDataApi, requestOptions);
        if (response.ok) {
            const result = await response.json();
            return result;
        } else {
            console.log("Fetch error %s return %d", getSensorDataApi, response.status);
            logEvent(analytics, "error", { status_code: response.status });
            if (response.status === 401) {
                return redirect("/login");
            } else {
                throw new Response("", { status: response.status });
            }
        }
    } catch (error) {
        console.error('Fetch error:', error);
    }
};